import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Clock, AlertTriangle, Radio, ChevronRight, HeartPulse } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

interface LivePatientData {
  patient_id: string;
  patient_nickname: string;
  patient_photo_url: string | null;
  active_shift: {
    id: string;
    caregiver_id: string;
    caregiver_name: string | null;
    check_in_at: string;
    planned_start: string | null;
    planned_end: string | null;
    tasks_total: number;
    tasks_completed: number;
    incidents_count: number;
    vital_alerts_count: number;
  } | null;
}

type FilterType = 'all' | 'active' | 'none' | 'incidents' | 'late';

interface LiveShiftPanelProps {
  companyId: string;
}

function isLateCheckin(item: LivePatientData): boolean {
  if (!item.active_shift || !item.active_shift.planned_start) return false;
  const checkInDate = new Date(item.active_shift.check_in_at);
  const plannedStart = item.active_shift.planned_start;
  const [hours, minutes] = plannedStart.split(':').map(Number);
  const plannedDate = new Date(checkInDate);
  plannedDate.setHours(hours, minutes, 0, 0);
  return checkInDate > plannedDate;
}

export function LiveShiftPanel({ companyId }: LiveShiftPanelProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  const { data: liveData = [], isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['live-shifts', companyId],
    queryFn: async (): Promise<LivePatientData[]> => {
      // 1. Get company patients
      const { data: assignments } = await supabase
        .from('company_patient_assignments')
        .select('patient_id')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (!assignments?.length) return [];

      const patientIds = assignments.map(a => a.patient_id);

      // 2. Get patient details
      const { data: patients } = await supabase
        .from('patients')
        .select('id, nickname, photo_url')
        .in('id', patientIds);

      if (!patients) return [];

      // 3. Get active shifts
      const { data: activeShifts } = await supabase
        .from('shifts')
        .select('id, patient_id, caregiver_id, check_in_at, shift_template_id')
        .in('patient_id', patientIds)
        .eq('status', 'active');

      // 4. Get caregiver profiles
      const caregiverIds = [...new Set((activeShifts || []).map(s => s.caregiver_id))];
      let caregiverProfiles: Record<string, string> = {};
      if (caregiverIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', caregiverIds);
        if (profiles) {
          caregiverProfiles = Object.fromEntries(profiles.map(p => [p.user_id, p.full_name || '']));
        }
      }

      // 5. Get shift templates for planned times
      const templateIds = [...new Set((activeShifts || []).filter(s => s.shift_template_id).map(s => s.shift_template_id!))];
      let templates: Record<string, { default_start_time: string; default_end_time: string; has_variable_hours: boolean | null; day_specific_times: any }> = {};
      if (templateIds.length > 0) {
        const { data: tmpl } = await supabase
          .from('shift_templates')
          .select('id, default_start_time, default_end_time, has_variable_hours, day_specific_times')
          .in('id', templateIds);
        if (tmpl) {
          templates = Object.fromEntries(tmpl.map(t => [t.id, t]));
        }
      }

      // 6. Get task instances for active shifts
      const shiftIds = (activeShifts || []).map(s => s.id);
      const taskCounts: Record<string, { total: number; completed: number }> = {};
      if (shiftIds.length > 0) {
        const { data: instances } = await supabase
          .from('shift_task_instances')
          .select('shift_id, status')
          .in('shift_id', shiftIds);
        if (instances) {
          for (const inst of instances) {
            if (!taskCounts[inst.shift_id]) taskCounts[inst.shift_id] = { total: 0, completed: 0 };
            taskCounts[inst.shift_id].total++;
            if (inst.status === 'completed') taskCounts[inst.shift_id].completed++;
          }
        }
      }

      // 7. Get incidents and vital alerts for active shifts
      const incidentCounts: Record<string, number> = {};
      const vitalAlertCounts: Record<string, number> = {};
      if (shiftIds.length > 0) {
        const { data: executions } = await supabase
          .from('task_executions')
          .select('shift_id, adhoc_task_type, task_id, is_alert')
          .in('shift_id', shiftIds);

        if (executions) {
          const taskIdsWithType = [...new Set(executions.filter(e => e.task_id).map(e => e.task_id!))];
          let incidentTaskIds = new Set<string>();
          if (taskIdsWithType.length > 0) {
            const { data: tasks } = await supabase
              .from('care_plan_tasks')
              .select('id, task_type')
              .in('id', taskIdsWithType)
              .eq('task_type', 'incident');
            if (tasks) incidentTaskIds = new Set(tasks.map(t => t.id));
          }

          for (const exec of executions) {
            const isIncident = exec.adhoc_task_type === 'incident' || (exec.task_id && incidentTaskIds.has(exec.task_id));
            if (isIncident && exec.shift_id) {
              incidentCounts[exec.shift_id] = (incidentCounts[exec.shift_id] || 0) + 1;
            }
            if (exec.is_alert && exec.shift_id) {
              vitalAlertCounts[exec.shift_id] = (vitalAlertCounts[exec.shift_id] || 0) + 1;
            }
          }
        }
      }

      // Build result
      const now = new Date();
      const dayOfWeek = now.getDay();

      return patients.map(patient => {
        const shift = (activeShifts || []).find(s => s.patient_id === patient.id);
        if (!shift) {
          return {
            patient_id: patient.id,
            patient_nickname: patient.nickname,
            patient_photo_url: patient.photo_url,
            active_shift: null,
          };
        }

        let planned_start: string | null = null;
        let planned_end: string | null = null;

        if (shift.shift_template_id && templates[shift.shift_template_id]) {
          const tmpl = templates[shift.shift_template_id];
          if (tmpl.has_variable_hours && tmpl.day_specific_times?.[dayOfWeek.toString()]) {
            planned_start = tmpl.day_specific_times[dayOfWeek.toString()].start;
            planned_end = tmpl.day_specific_times[dayOfWeek.toString()].end;
          } else {
            planned_start = tmpl.default_start_time;
            planned_end = tmpl.default_end_time;
          }
        }

        const counts = taskCounts[shift.id] || { total: 0, completed: 0 };

        return {
          patient_id: patient.id,
          patient_nickname: patient.nickname,
          patient_photo_url: patient.photo_url,
          active_shift: {
            id: shift.id,
            caregiver_id: shift.caregiver_id,
            caregiver_name: caregiverProfiles[shift.caregiver_id] || null,
            check_in_at: shift.check_in_at,
            planned_start,
            planned_end,
            tasks_total: counts.total,
            tasks_completed: counts.completed,
            incidents_count: incidentCounts[shift.id] || 0,
            vital_alerts_count: vitalAlertCounts[shift.id] || 0,
          },
        };
      }).sort((a, b) => {
        if (a.active_shift && !b.active_shift) return -1;
        if (!a.active_shift && b.active_shift) return 1;
        return a.patient_nickname.localeCompare(b.patient_nickname);
      });
    },
    enabled: !!companyId,
    refetchInterval: 5 * 60 * 1000, // 5min fallback; realtime invalidates instantly
    staleTime: 30 * 1000,
  });

  // Realtime invalidation: refetch only when relevant rows change for this company's patients.
  useEffect(() => {
    if (!companyId) return;
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['live-shifts', companyId] });
    };
    const channel = supabase
      .channel(`live-shifts-${companyId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shifts' }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shift_task_instances' }, invalidate)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'task_executions' }, invalidate)
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, queryClient]);

  const activeCount = liveData.filter(p => p.active_shift).length;
  const inactiveCount = liveData.filter(p => !p.active_shift).length;
  const incidentsCount = liveData.filter(p => (p.active_shift?.incidents_count || 0) > 0).length;
  const lateCount = liveData.filter(p => isLateCheckin(p)).length;

  const filters: { key: FilterType; label: string; count: number }[] = [
    { key: 'all', label: t('live.filterAll'), count: liveData.length },
    { key: 'active', label: t('live.filterActive'), count: activeCount },
    { key: 'none', label: t('live.filterNoShift'), count: inactiveCount },
    { key: 'incidents', label: t('live.filterWithIncidents'), count: incidentsCount },
    { key: 'late', label: t('live.filterLateCheckin'), count: lateCount },
  ];

  const filteredData = liveData.filter(item => {
    switch (filter) {
      case 'active': return item.active_shift !== null;
      case 'none': return item.active_shift === null;
      case 'incidents': return (item.active_shift?.incidents_count || 0) > 0;
      case 'late': return isLateCheckin(item);
      default: return true;
    }
  });

  const updatedAgo = dataUpdatedAt
    ? formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: false, locale })
    : '';

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 w-full" />)}
      </div>
    );
  }

  const formatTime = (time: string | null) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  return (
    <div className="space-y-4">
      {/* Status bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm gap-2">
        <div className="flex items-center gap-3">
          {activeCount > 0 && (
            <span className="flex items-center gap-1.5 font-medium text-success">
              <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
              {t('live.activeCount', { count: activeCount })}
            </span>
          )}
          {inactiveCount > 0 && (
            <span className="text-muted-foreground">
              {t('live.noShiftCount', { count: inactiveCount })}
            </span>
          )}
        </div>
        {updatedAgo && (
          <span className="text-xs text-muted-foreground">
            {t('live.updatedAgo', { time: updatedAgo })}
          </span>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 overflow-x-auto pb-1 -mb-1">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap border ${
              filter === f.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {f.label}
            <span className={`text-[10px] ${filter === f.key ? 'opacity-80' : 'opacity-60'}`}>
              ({f.count})
            </span>
          </button>
        ))}
      </div>

      {/* Cards */}
      {filteredData.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Radio className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p>{t('live.noActiveShifts')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredData.map(item => (
            <Card
              key={item.patient_id}
              className="cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => navigate(`/app/patients/${item.patient_id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <SignedAvatar
                    src={item.patient_photo_url}
                    bucket="patient-photos"
                    className="h-11 w-11 mt-0.5"
                    fallback={
                      <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                        {item.patient_nickname.charAt(0).toUpperCase()}
                      </span>
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{item.patient_nickname}</p>
                      {item.active_shift && (
                        <Badge className="bg-success/10 text-success border-success/30 text-[10px] px-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse mr-1" />
                          {t('company.shiftActive')}
                        </Badge>
                      )}
                      {item.active_shift && item.active_shift.vital_alerts_count > 0 && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 animate-pulse">
                          <HeartPulse className="h-3 w-3 mr-0.5" />
                          {item.active_shift.vital_alerts_count}
                        </Badge>
                      )}
                    </div>

                    {item.active_shift ? (
                      <div className="mt-1.5 space-y-1.5">
                        <p className="text-sm text-muted-foreground">
                          {item.active_shift.caregiver_name || t('company.unknownCaregiver')}
                        </p>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {item.active_shift.planned_start && item.active_shift.planned_end && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(item.active_shift.planned_start)} – {formatTime(item.active_shift.planned_end)}
                            </span>
                          )}
                          <span>·</span>
                          <span>
                            {t('live.startedAgo', {
                              time: formatDistanceToNow(new Date(item.active_shift.check_in_at), { locale })
                            })}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Progress
                            value={item.active_shift.tasks_total > 0
                              ? Math.round((item.active_shift.tasks_completed / item.active_shift.tasks_total) * 100)
                              : 0}
                            className="h-2 flex-1"
                            indicatorClassName={
                              item.active_shift.tasks_total > 0
                                ? (item.active_shift.tasks_completed / item.active_shift.tasks_total) >= 0.8
                                  ? 'bg-success'
                                  : (item.active_shift.tasks_completed / item.active_shift.tasks_total) >= 0.5
                                    ? 'bg-warning'
                                    : 'bg-primary'
                                : 'bg-muted'
                            }
                          />
                          <span className="text-xs font-medium min-w-[2.5rem] text-right">
                            {item.active_shift.tasks_total > 0
                              ? `${Math.round((item.active_shift.tasks_completed / item.active_shift.tasks_total) * 100)}%`
                              : '—'}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-muted-foreground">
                            {item.active_shift.tasks_completed}/{item.active_shift.tasks_total} {t('live.taskProgress')}
                          </span>
                          {item.active_shift.incidents_count > 0 ? (
                            <span className="flex items-center gap-1 text-destructive font-medium">
                              <AlertTriangle className="h-3 w-3" />
                              {item.active_shift.incidents_count} {t('live.incidents')}
                            </span>
                          ) : (
                            <span className="text-success">{t('live.noIncidents')}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-warning font-medium mt-1">
                        {t('company.noActiveShift')}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
